import type { JobSnapshot, SequencedEvent } from "./events";

export type StartResult =
  | { state: "ready" }
  | { state: "running"; jobId: string; large: boolean }
  | { state: "error"; message: string; reason?: string; status: number };

/** Ask the server to start rebuilding a reading, or to say it already has. */
export async function startRebuildJob(
  readingId: string,
  mode: "quick" | "deep",
): Promise<StartResult> {
  let res: Response;
  try {
    res = await fetch("/api/jobs", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ readingId, mode }),
    });
  } catch {
    return {
      state: "error",
      status: 0,
      message: "Could not reach Syft. Check your connection and try again.",
    };
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      state: "error",
      status: res.status,
      reason: body.reason,
      message:
        body.error ?? "Could not rebuild this reading. Please try again.",
    };
  }
  return body as StartResult;
}

export interface JobWatchers {
  onEvent: (event: SequencedEvent) => void;
}

/**
 * Follow a job's live stream. The browser reconnects by itself when the
 * connection drops and says where it got to, so nothing is repeated or lost.
 * Returns a function that stops watching; the job itself carries on.
 */
export function watchJob(jobId: string, { onEvent }: JobWatchers): () => void {
  const source = new EventSource(`/api/jobs/${jobId}/events`);
  source.onmessage = (message) => {
    let event: SequencedEvent;
    try {
      event = JSON.parse(message.data) as SequencedEvent;
    } catch {
      return;
    }
    onEvent(event);
    if (event.type === "done" || event.type === "failed") source.close();
  };
  return () => source.close();
}

export async function listJobs(): Promise<JobSnapshot[] | null> {
  try {
    const res = await fetch("/api/jobs", { credentials: "same-origin" });
    if (!res.ok) return null;
    return ((await res.json()) as { jobs: JobSnapshot[] }).jobs;
  } catch {
    return null;
  }
}

export async function getJobSnapshot(
  jobId: string,
): Promise<JobSnapshot | null> {
  try {
    const res = await fetch(`/api/jobs/${jobId}`, {
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    return ((await res.json()) as { job: JobSnapshot }).job;
  } catch {
    return null;
  }
}
